import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import { ControlledField, FormDialog } from '@shared/components';
import { useCompletePeriodicReview } from '../../api/periodicReviews.api';
import { EPeriodicReviewOutcome, PERIODIC_REVIEW_OUTCOME_LABELS } from '../../types/periodicReviews.types';
import type { TPeriodicReviewItemType } from '../../types/periodicReviews.types';

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/config.ts (MAX_COMMENT_LENGTH)
const MAX_REVIEW_NOTE_LENGTH = 2000;

const schema = yup
  .object({
    outcome: yup.string().oneOf(Object.values(EPeriodicReviewOutcome)).required(),
    note: yup.string().max(MAX_REVIEW_NOTE_LENGTH, `Review note cannot exceed ${MAX_REVIEW_NOTE_LENGTH} characters.`),
    addNoteAsComment: yup.boolean().required(),
  })
  .required();

type TFormData = yup.InferType<typeof schema>;

const DEFAULT_FORM_DATA: TFormData = {
  outcome: EPeriodicReviewOutcome.NO_CHANGES_REQUIRED,
  note: '',
  addNoteAsComment: false,
};

const OUTCOME_OPTIONS = Object.values(EPeriodicReviewOutcome).map((outcome) => ({
  value: outcome,
  label: PERIODIC_REVIEW_OUTCOME_LABELS[outcome],
}));

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  item: TDocument | TFile;
  itemType: TPeriodicReviewItemType;
  onCompleted: () => void;
};

const CompletePeriodicReviewDialog = ({ isOpen, setIsOpen, item, itemType, onCompleted }: TProps) => {
  const completeReview = useCompletePeriodicReview();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: DEFAULT_FORM_DATA,
  });
  const outcome = watch('outcome');
  const note = watch('note');
  const hasNote = Boolean(note?.trim());

  useEffect(() => {
    if (!isOpen) return;
    reset(DEFAULT_FORM_DATA);
  }, [isOpen, reset]);

  useEffect(() => {
    setValue('addNoteAsComment', outcome === EPeriodicReviewOutcome.CHANGES_REQUIRED);
  }, [outcome, setValue]);

  const onSubmit = async (data: TFormData) => {
    await completeReview.mutateAsync({
      id: item._id,
      itemType,
      outcome: data.outcome as EPeriodicReviewOutcome,
      note: data.note?.trim() || undefined,
      addNoteAsComment: data.addNoteAsComment,
    });
    setIsOpen(false);
    onCompleted();
  };

  return (
    <FormDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Complete review"
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Complete"
      isSubmitting={completeReview.isPending}
    >
      <div className="flex flex-col gap-4">
        <ControlledField name="outcome" control={control} errors={errors} inputType="radio" options={OUTCOME_OPTIONS} />
        <ControlledField
          label="Review note"
          name="note"
          control={control}
          errors={errors}
          inputType="textarea"
          placeholder="Review note"
          maxLength={MAX_REVIEW_NOTE_LENGTH}
          resize="vertical"
        />
        <ControlledField
          name="addNoteAsComment"
          control={control}
          errors={errors}
          inputType="checkbox"
          label="Add note as comment"
          disabled={!hasNote}
        />
      </div>
    </FormDialog>
  );
};

export default CompletePeriodicReviewDialog;
